import {
  ContactListType,
  CreateEditContactType,
  defaultContact,
} from "utils/interfaces";
import { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import CreateEditUser from "../common/CreateEditUser";
import ConfirmPrimaryPopUp from "components/main-view/utils/pop-up-panels/common/ConfirmPrimaryPopUp";
import SuccessPopUpMessage from "components/main-view/utils/pop-up-panels/common/SuccessPopUpMessage";
import { MainContext } from "components/utils/Contexts";
import {
  createRecordRequest,
  getCollectionRequest,
} from "services/apiRequests";
import UserCardsList from "../common/UserCardsList";
import {
  defaultBgVariants,
  defaultPanelVariants,
} from "../common/defaultValues";
import PopUpHeader from "../common/PopUpHeader";
import { logoutFunction } from "services/authRequests";

interface AddContactsPanelProps {
  fromCreateNew: boolean;
  contacts: ContactListType[];
  admins: ContactListType[];
  setContacts: React.Dispatch<React.SetStateAction<ContactListType[]>>;
  setAdmins: React.Dispatch<React.SetStateAction<ContactListType[]>>;
  handleClosePopUp: () => void;
}

const AddContactsPanel: React.FC<AddContactsPanelProps> = ({
  fromCreateNew,
  contacts,
  admins,
  setContacts,
  setAdmins,
  handleClosePopUp,
}) => {
  // States definition
  const [contactData, setContactData] = useState<ContactListType[] | null>(
    null
  );
  const [createNewContact, setCreateNewContact] = useState(false);
  const [showSuccessPopUp, setShowSuccessPopUp] = useState(false);
  const [showConfirmPrimaryPopUp, setShowConfirmPrimaryPopUp] = useState(false);
  const [lastSelectedContact, setLastSelectedContact] =
    useState<ContactListType>(defaultContact);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Get the context values
  const { userRole, contextSchoolByRole } = useContext(MainContext);

  /**
   * Get the data each time the pop up appears
   */
  useEffect(() => {
    getContactData();
  }, []);

  /**
   * Timer for the success pop up message
   */
  useEffect(() => {
    if (showSuccessPopUp) {
      // Starting the timer
      const timer = setTimeout(() => {
        // Once finished, close the pop up
        setShowSuccessPopUp(false);
        // And forget the last selected id
        setLastSelectedContact(defaultContact);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessPopUp]);

  /**
   * Definition of the function when pressed back button
   */
  const handlePressBack = () => {
    // If create new was pressed, then come back to the regular option
    if (createNewContact) setCreateNewContact(false);
    // Otherwise, close the pop up
    else handleClosePopUp();
  };

  /**
   * Function to load the data
   */
  const getContactData = async () => {
    // First of all, start the animation
    setIsLoadingData(true);

    // Check if the context school is defined
    if (!contextSchoolByRole && fromCreateNew) {
      setIsLoadingData(false);
      setContactData(admins);
      return [];
    }

    // Get the data
    const response = await getCollectionRequest(
      `/api/users/customer-admins?schoolId=${contextSchoolByRole._id}`
    );

    // And stop the loading data
    setIsLoadingData(false);

    // Check if the response is successful
    if (!response.successful) {
      // Check if it's not successful because of a token expiration
      if (response.logout) {
        // alert(response.message);
        return await logoutFunction();
      }

      return [];
    }

    // Get the data
    const data = response.content;

    // Set the state
    setContactData(data);
    setAdmins(data);

    return data;
  };

  /**
   * Function executed when clicking "add as primary contact" on the
   * ConfirmPrimaryPopUp panel
   */
  const addAsPrimaryContact = () => {
    // Put the last contact as the first on the list of contacts
    setContacts((currentValue) => [
      ...currentValue.slice(-1),
      ...currentValue.slice(0, -1),
    ]);

    // Close the pop up
    setShowConfirmPrimaryPopUp(false);

    // And display the pop up successful message
    setShowSuccessPopUp(true);
  };

  /**
   * Function executed when clicking "add as regular contact" on the
   * ConfirmPrimaryPopUp panel
   */
  const addAsAdditionalContact = () => {
    // Just close the pop up
    setShowConfirmPrimaryPopUp(false);

    // And display the pop up successful message
    setShowSuccessPopUp(true);
  };

  /**
   * Definition of the Add or remove a contact from the list
   * @param id of the selected id in the pop up menu
   * @param action "add" or "remove" "selectedData._id" from "id"
   */
  const selectContactAction = (
    contact: ContactListType,
    action: "add" | "remove"
  ) => {
    // Add to the current selected contact data ids
    if (action === "add") {
      // Add to the ids list (and ensure prevent duplicated)
      setContacts([...new Set([...contacts, contact])]);

      // Define the last selected contact
      setLastSelectedContact(contact);

      // If there is at least one contact when adding, display the pop up window
      // to ask if the following one wants to be primary or additional
      if (contacts.length > 0) setShowConfirmPrimaryPopUp(true);
      else setShowSuccessPopUp(true);
    }
    // Or delete from it
    else if (action === "remove") {
      setContacts(
        contacts.filter((user: ContactListType) => {
          if (fromCreateNew) {
            return user.tempId !== contact.tempId;
          } else {
            return user._id !== contact._id;
          }
        })
      );

      // Force pop up message to dissapear
      setShowSuccessPopUp(false);
    }
  };

  /**
   * Function used when creating a new contact inside the
   * add contact panel
   * @param data
   * @param processedData
   * @returns
   */
  const handleCreateContact = async (
    data: CreateEditContactType,
    processedData: CreateEditContactType
  ) => {
    // Check if the school of context is new or created
    if (fromCreateNew) {
      // Create contact in the current list
      // Define a temporary id
      const tempId = Date.now();
      // And add it to the current data
      const newContact = { ...data, tempId: tempId.toString() };

      // Define the new admin list
      const newAdminList = [...admins, newContact as ContactListType];

      // Update the list of admins to show
      setAdmins(newAdminList);

      // And update the list of contacts to show
      setContactData(newAdminList);

      // Add to the contact list
      setContacts((currentList) => [...currentList, newContact as ContactListType]);
      // And set the last contact
      setLastSelectedContact(data as ContactListType);

      // And display the confirm primary contact pop up
      if (newAdminList.length > 1) setShowConfirmPrimaryPopUp(true);
      // Otherwise, just show the success pop up
      else setShowSuccessPopUp(true);
    }
    // For a currently existing school
    else {
      // Request for creating new contact
      const response = await createRecordRequest(
        processedData,
        "/api/users/customer-admins"
      );

      // Check if the response is successful
      if (!response.successful) {
        // alert(response.message);
        return;
      }

      // And refresh the data
      const adminList = await getContactData();

      // Get the created admin
      const createdAdmin = adminList.filter(
        (admin: ContactListType) => admin.email === data.email
      );

      // Check that the user exists
      if (!(createdAdmin && createdAdmin.length > 0)) {
        // Come back to the regular view
        setCreateNewContact(false);
        console.log("Failed when looking for the created contact");
        return;
      }

      // Add to the current list of users 
      setContacts((currentList) => [...currentList, ...createdAdmin])
      // And set the last contact
      setLastSelectedContact(createdAdmin[0]);

      // Display the confirm primary contact pop up
      if (contacts.length > 0) setShowConfirmPrimaryPopUp(true);
      // Otherwise, just show the success pop up
      else setShowSuccessPopUp(true);
    }

    // Come back to the regular view
    setCreateNewContact(false);
  };

  return (
    <>
      <motion.div
        key="add-contact-blurred-background"
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
        variants={defaultBgVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        onClick={handleClosePopUp}
      />
      {/* List of contacts to be assigned */}
      {!createNewContact && !showConfirmPrimaryPopUp && (
        <motion.div
          key="add-contact"
          className="fixed w-[462px] h-fit left-0 right-0 ml-auto mr-auto bottom-5 p-6 bg-white rounded-3xl flex-col justify-start items-start gap-6 inline-flex z-30 ease-in-out duration-150"
          variants={defaultPanelVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.1, ease: "easeOut" }}
        >
          <div className="w-full h-fit max-h-[80vh] flex-col justify-start items-start gap-6 inline-flex relative">
            <PopUpHeader
              key="add-contact-header"
              title="Contacts"
              handlePressBack={handlePressBack}
              createNewOptions={["master admin", "customer admin"].includes(userRole) ? {
                text: "Create new",
                onClick: () => setCreateNewContact(true),
              } : undefined}
            />
            <UserCardsList
              key="add-contact-list"
              userList={contactData}
              selectedUsers={contacts}
              primaryTags={true}
              isLoadingData={isLoadingData}
              selectAction={selectContactAction}
            />
            <SuccessPopUpMessage
              key="success-contact-message"
              name={
                lastSelectedContact.firstName +
                " " +
                lastSelectedContact.lastName
              }
              nameType="Contact"
              showSuccessPopUp={showSuccessPopUp}
              setShowSuccessPopUp={setShowSuccessPopUp}
              dismissAction={() => {}}
            />
          </div>
        </motion.div>
      )}
      {/* Create a new contact */}
      {createNewContact && (
        <CreateEditUser
          confirmButtonText="Create contact"
          usersListToCheck={admins}
          showPrimaryOption={false}
          handlePressBack={() => setCreateNewContact(false)}
          handleSaveFunction={handleCreateContact}
        />
      )}
      {/* Pop up to double check the primary pop up */}
      {showConfirmPrimaryPopUp && (
        <ConfirmPrimaryPopUp
          key="confirm-primary-contact"
          title="Add as Primary contact"
          paragraphs={[
            "Would you like to add this contact as your primary contact for this school?",
            "Primary contacts serve as the first point of communication when engaging with the organization.",
          ]}
          options={[
            { text: "Add as primary contact", onClick: addAsPrimaryContact },
            { text: "Add as regular contact", onClick: addAsAdditionalContact },
          ]}
          handleClosePopUp={() => setShowConfirmPrimaryPopUp(false)}
        />
      )}
    </>
  );
};

export default AddContactsPanel;
